import React, { useEffect, useRef, useState, useCallback } from "react";
import * as API from "../api";
import { TEXT, TEXT2, TEXT3, BLUE, BORDER, DANGER, SUCCESS, WARNING, FONT, GLASS_BORDER_COLOR } from "../shared";

// VELLA 2.0 — NOTIFICATION BELL
// Liquid-glass bell button + dropdown panel. Polls /api/notifications/feed
// every 60s, shows an unread dot when items are newer than the user's last
// "seen" marker (persisted to localStorage so opening the bell resets the
// dot across reloads). Read-state is client-side only — the backend feed
// is materialized on read from inbox / posts / campaigns, so there's no
// per-notification "mark as read" call to make.

const POLL_MS = 60_000;
const STORAGE_KEY = "vella_notif_lastSeen";

function timeAgo(ts) {
  if (!ts) return "";
  const ms = Date.now() - new Date(ts).getTime();
  if (ms < 60_000) return "just now";
  const min = Math.floor(ms / 60_000);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.floor(hr / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(ts).toLocaleDateString();
}

function dotColor(item) {
  if (item.severity === "warning") return WARNING;
  if (item.kind === "lead") return SUCCESS;
  if (item.kind === "post_failed" || item.kind === "campaign_failed") return DANGER;
  return BLUE;
}

export default function NotificationBell({ onNavigate, isMobile }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastSeen, setLastSeen] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || ""; } catch { return ""; }
  });
  const wrapRef = useRef(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await API.getNotificationsFeed({ limit: 25 });
      setItems(Array.isArray(data?.items) ? data.items : []);
    } catch (err) {
      setError(err?.message || "Could not load notifications");
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load + 60s polling. We pause polling when the tab is hidden
  // so a backgrounded tab doesn't burn rate-limit on the API.
  useEffect(() => {
    refresh();
    let id = setInterval(refresh, POLL_MS);
    function onVis() {
      clearInterval(id);
      if (!document.hidden) {
        refresh();
        id = setInterval(refresh, POLL_MS);
      }
    }
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(id); document.removeEventListener("visibilitychange", onVis); };
  }, [refresh]);

  // Click-outside / Escape to close
  useEffect(() => {
    if (!open) return;
    function onDown(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    function onKey(e) { if (e.key === "Escape") setOpen(false); }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [open]);

  // Mark "seen" the moment the user opens the bell — newest item's
  // timestamp becomes the high-water mark. Subsequent polls compare
  // against this so the unread dot only re-appears for genuinely new items.
  function openPanel() {
    setOpen(true);
    if (items.length > 0) {
      const newest = items[0]?.createdAt;
      if (newest) {
        setLastSeen(newest);
        try { localStorage.setItem(STORAGE_KEY, newest); } catch {}
      }
    }
  }

  function handleAction(item) {
    setOpen(false);
    if (!onNavigate || !item.action) return;
    if (item.action.type === "open_inbox") onNavigate("inbox");
    else if (item.action.type === "open_post") onNavigate("schedule");
    else if (item.action.type === "open_campaign") onNavigate("ad-campaigns");
  }

  const unread = lastSeen
    ? items.filter((it) => new Date(it.createdAt) > new Date(lastSeen)).length
    : items.length;

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <button
        type="button"
        onClick={() => (open ? setOpen(false) : openPanel())}
        aria-label={`Notifications${unread > 0 ? ` (${unread} unread)` : ""}`}
        className="liquid-hover liquid-press"
        style={{
          width: 36, height: 36, borderRadius: 14,
          background: "rgba(255,255,255,0.55)",
          backdropFilter: "blur(14px)", WebkitBackdropFilter: "blur(14px)",
          border: `1px solid ${GLASS_BORDER_COLOR}`,
          color: TEXT2, display: "flex", alignItems: "center", justifyContent: "center",
          cursor: "pointer", position: "relative",
          transition: "transform 220ms cubic-bezier(0.22, 1, 0.36, 1), background 220ms ease-out",
        }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 01-3.46 0" />
        </svg>
        {unread > 0 && (
          <div style={{ position: "absolute", top: 4, right: 4, minWidth: 8, height: 8, padding: unread > 9 ? "0 4px" : 0, borderRadius: unread > 9 ? 6 : "50%", background: DANGER, color: "#FFF", fontSize: 9, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 1, border: "1px solid #FFF" }}>
            {unread > 9 ? unread : ""}
          </div>
        )}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Notifications"
          style={{
            position: "absolute",
            right: isMobile ? -8 : 0,
            top: "calc(100% + 8px)",
            width: isMobile ? 320 : 360,
            maxHeight: 480,
            background: "rgba(255,255,255,0.92)",
            backdropFilter: "blur(40px) saturate(200%)",
            WebkitBackdropFilter: "blur(40px) saturate(200%)",
            border: "1px solid rgba(255,255,255,0.65)",
            borderRadius: 18,
            boxShadow: "0 1px 0 rgba(255,255,255,0.95) inset, 0 24px 60px -20px rgba(11,107,211,0.28)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            fontFamily: FONT,
            zIndex: 80,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderBottom: `1px solid ${BORDER}` }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: TEXT, letterSpacing: "-0.01em" }}>Notifications</span>
            <button
              type="button"
              onClick={refresh}
              style={{ background: "transparent", border: "none", cursor: "pointer", color: TEXT3, fontSize: 11, fontWeight: 600 }}
            >
              {loading ? "Refreshing…" : "Refresh"}
            </button>
          </div>

          <div style={{ overflowY: "auto", flex: 1, minHeight: 0 }}>
            {error ? (
              <div style={{ padding: 20, fontSize: 13, color: DANGER, textAlign: "center" }}>{error}</div>
            ) : loading && items.length === 0 ? (
              <div style={{ padding: 24, fontSize: 12, color: TEXT3, textAlign: "center" }}>Loading…</div>
            ) : items.length === 0 ? (
              <div style={{ padding: 28, fontSize: 13, color: TEXT3, textAlign: "center" }}>
                You're all caught up.
              </div>
            ) : (
              items.map((item) => {
                const isUnread = lastSeen ? new Date(item.createdAt) > new Date(lastSeen) : true;
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => handleAction(item)}
                    style={{
                      display: "flex", alignItems: "flex-start", gap: 10,
                      width: "100%", padding: "12px 16px",
                      background: isUnread ? "rgba(59,130,246,0.06)" : "transparent",
                      border: "none", borderBottom: `1px solid ${BORDER}`,
                      cursor: "pointer", textAlign: "left",
                      transition: "background 0.15s",
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(59,130,246,0.08)"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = isUnread ? "rgba(59,130,246,0.06)" : "transparent"; }}
                  >
                    <div style={{ width: 8, height: 8, borderRadius: "50%", background: dotColor(item), marginTop: 6, flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: isUnread ? 700 : 500, color: TEXT, marginBottom: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {item.title}
                      </div>
                      {item.body && (
                        <div style={{ fontSize: 12, color: TEXT2, lineHeight: 1.45, overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                          {item.body}
                        </div>
                      )}
                      <div style={{ fontSize: 11, color: TEXT3, marginTop: 4 }}>
                        {item.platform ? `${item.platform} · ` : ""}{timeAgo(item.createdAt)}
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>

          <div style={{ padding: "8px 16px", borderTop: `1px solid ${BORDER}`, fontSize: 11, color: TEXT3, textAlign: "center" }}>
            Synced every {POLL_MS / 1000}s · Showing last 7 days
          </div>
        </div>
      )}
    </div>
  );
}
